<template>
  <main id="main" class="main">
    <div class="pagetitle">
      <h1>WARMING ROOMS</h1>
      <nav>
        <ol class="breadcrumb breadJa">
          <router-link :to="{ name: 'home' }" class="breadcrumb-item">Home</router-link>
          <router-link :to="{ name: 'warming-rooms' }" class="breadcrumb-item">Warming Rooms</router-link>
        </ol>
      </nav>
    </div>

    <section class="section">
      <div class="row g-2">
        
        <!-- LEFT: Room Details & Controls (33%) -->
        <div class="col-lg-4">
          <div class="card shadow-sm" style="height: calc(100vh - 180px);">
            <div class="card-body p-0 d-flex flex-column h-100">
              <!-- Header -->
              <div class="p-2 border-bottom bg-light">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0 fw-bold" style="font-size: 0.9rem;">
                    <i class="bi bi-door-open-fill me-1" style="color: var(--primary-color);"></i>
                    Rooms
                  </h6>
                  <button
                    class="btn btn-sm btn-chatroom btn-primary-chatroom"
                    @click="openCreateModal"
                    :disabled="loading"
                  >
                    <i class="bi bi-plus-circle"></i>
                  </button>
                </div>
                <div class="d-flex gap-1">
                  <select class="form-select form-select-sm" v-model="statusFilter" @change="fetchRooms" style="font-size: 0.8rem;">
                    <option value="">All</option>
                    <option value="STOPPED">Stopped</option>
                    <option value="ACTIVE">Active</option>
                    <option value="PAUSED">Paused</option>
                    <option value="FINISHED">Finished</option>
                  </select>
                  <button
                    class="btn btn-sm btn-chatroom btn-outline-secondary"
                    @click="fetchRooms"
                    :disabled="loading"
                  >
                    <i class="bi bi-arrow-repeat"></i>
                  </button>
                </div>
              </div>

              <!-- Room List -->
              <div class="flex-grow-1 overflow-auto p-2">
                <div v-if="loading" class="text-center py-4">
                  <div class="spinner-border spinner-border-sm text-primary"></div>
                </div>

                <div v-else-if="rooms.length === 0" class="text-center py-4 text-muted">
                  <i class="bi bi-inbox" style="font-size: 2rem;"></i>
                  <p class="mt-2 small">No rooms</p>
                </div>

                <div v-else>
                  <div
                    v-for="room in rooms"
                    :key="room.id"
                    class="room-card-detailed mb-2 border rounded cursor-pointer overflow-hidden"
                    :class="{ 'room-card-active': selectedRoom?.id === room.id }"
                    @click="selectRoom(room)"
                  >
                    <!-- Card Header with Gradient -->
                    <div class="room-card-header p-3" :class="{
                      'bg-gradient-secondary': room.status === 'STOPPED',
                      'bg-gradient-success': room.status === 'ACTIVE',
                      'bg-gradient-warning': room.status === 'PAUSED',
                      'bg-gradient-primary': room.status === 'FINISHED'
                    }">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <div class="text-white fw-bold" style="font-size: 0.95rem;">
                          <i class="bi bi-door-open me-2"></i>
                          {{ room.name }}
                        </div>
                        <span class="badge bg-white text-dark" style="font-size: 0.75rem; padding: 4px 8px;">
                          {{ room.status }}
                        </span>
                      </div>
                      <!-- Room Type Badge in Header -->
                      <div class="d-flex align-items-center gap-2">
                        <span 
                          class="badge flex-fill text-start d-flex align-items-center gap-2 text-white border border-white" 
                          style="font-size: 0.75rem; padding: 6px 10px; font-weight: 600; background: transparent;"
                        >
                          <i class="bi" :class="room.roomType === 'HUMAN_VS_BOT' ? 'bi-person-fill-check' : 'bi-robot'"></i>
                          <span>{{ room.roomType === 'HUMAN_VS_BOT' ? 'HUMAN vs BOT' : 'BOT vs BOT' }}</span>
                        </span>
                        <span v-if="room.roomType === 'HUMAN_VS_BOT' && room.aiEnabled" class="badge bg-warning text-dark" style="font-size: 0.7rem; padding: 5px 8px;">
                          <i class="bi bi-stars"></i> AI
                        </span>
                      </div>
                    </div>

                    <!-- Card Body with Details -->
                    <div class="room-card-body p-3 bg-white">

                      <!-- Script Info -->
                      <div class="mb-2">
                        <div class="small text-muted mb-1" style="font-size: 0.8rem;">
                          <i class="bi bi-file-text text-info me-1"></i>
                          <strong>Script:</strong>
                          <span v-if="room.roomType === 'HUMAN_VS_BOT' && room.aiEnabled" class="badge bg-secondary ms-1" style="font-size: 0.65rem;">Fallback</span>
                        </div>
                        <div class="badge bg-info bg-opacity-10 text-info w-100 text-start d-flex justify-content-between align-items-center" style="font-size: 0.8rem; padding: 6px 10px;">
                          <span>{{ room.scriptTitle || 'N/A' }}</span>
                          <span class="badge bg-info text-white ms-2" style="font-size: 0.7rem;">{{ room.totalLines || 0 }} lines</span>
                        </div>
                      </div>

                      <!-- Instances Info (BOT_VS_BOT) -->
                      <div v-if="room.roomType === 'BOT_VS_BOT'" class="mb-2">
                        <div class="small text-muted mb-1" style="font-size: 0.8rem;">
                          <i class="bi bi-arrow-left-right text-primary me-1"></i>
                          <strong>Instances:</strong>
                        </div>
                        <div class="d-flex gap-1 align-items-center" style="font-size: 0.75rem;">
                          <span class="badge bg-primary bg-opacity-10 text-primary flex-fill text-truncate" style="padding: 6px 10px;">
                            {{ formatInstanceDisplay(room.senderInstanceId) }}
                          </span>
                          <i class="bi bi-arrow-right text-muted"></i>
                          <span class="badge bg-success bg-opacity-10 text-success flex-fill text-truncate" style="padding: 6px 10px;">
                            {{ formatInstanceDisplay(room.receiverInstanceId) }}
                          </span>
                        </div>
                      </div>

                      <!-- Bot Instance & Whitelisted Number (HUMAN_VS_BOT) -->
                      <div v-if="room.roomType === 'HUMAN_VS_BOT'" class="mb-2">
                        <div class="small text-muted mb-1" style="font-size: 0.8rem;">
                          <i class="bi bi-robot text-success me-1"></i>
                          <strong>Bot Instance:</strong>
                        </div>
                        <div class="badge bg-success bg-opacity-10 text-success w-100 text-start" style="font-size: 0.75rem; padding: 6px 10px;">
                          {{ formatInstanceDisplay(room.senderInstanceId) }}
                        </div>
                        
                        <div class="small text-muted mb-1 mt-2" style="font-size: 0.8rem;">
                          <i class="bi bi-person-check-fill text-primary me-1"></i>
                          <strong>Whitelisted:</strong>
                        </div>
                        <div class="badge bg-primary bg-opacity-10 text-primary w-100 text-start" style="font-size: 0.75rem; padding: 6px 10px;">
                          <i class="bi bi-phone me-1"></i>
                          {{ room.whitelistedNumber || 'N/A' }}
                        </div>
                      </div>

                      <!-- Progress Info (only for BOT_VS_BOT) -->
                      <div v-if="room.roomType === 'BOT_VS_BOT'" class="mb-2">
                        <div class="small text-muted mb-1" style="font-size: 0.8rem;">
                          <i class="bi bi-bar-chart-steps text-warning me-1"></i>
                          <strong>Progress:</strong>
                        </div>
                        <div class="progress" style="height: 8px;">
                          <div 
                            class="progress-bar" 
                            :class="{
                              'bg-secondary': room.status === 'STOPPED',
                              'bg-success': room.status === 'ACTIVE',
                              'bg-warning': room.status === 'PAUSED',
                              'bg-primary': room.status === 'FINISHED'
                            }"
                            :style="{ width: ((room.currentSequence || 0) / (room.totalLines || 1) * 100) + '%' }"
                          ></div>
                        </div>
                        <div class="small text-muted text-end mt-1" style="font-size: 0.75rem;">
                          {{ room.currentSequence || 0 }} / {{ room.totalLines || 0 }} messages
                        </div>
                      </div>

                      <!-- Interval/Delay Info -->
                      <div class="mb-2">
                        <div class="small text-muted" style="font-size: 0.8rem;">
                          <i class="bi me-1" :class="room.roomType === 'HUMAN_VS_BOT' ? 'bi-hourglass-split text-warning' : 'bi-clock text-secondary'"></i>
                          <strong>{{ room.roomType === 'HUMAN_VS_BOT' ? 'Reply Delay:' : 'Interval:' }}</strong>
                          <span v-if="room.roomType === 'HUMAN_VS_BOT'">
                            {{ room.replyDelayMin || 0 }}s - {{ room.replyDelayMax || 0 }}s
                          </span>
                          <span v-else>
                            {{ formatIntervalDisplay(room.intervalMinSeconds, room.intervalMaxSeconds) }}
                          </span>
                        </div>
                      </div>
                      
                      <!-- Mode Info (Dry Run / Real Mode) - Single Line -->
                      <div class="mb-0">
                        <div class="small" style="font-size: 0.8rem;">
                          <i class="bi bi-gear-fill me-1" :class="room.sendRealMessage ? 'text-danger' : 'text-info'"></i>
                          <strong class="text-muted">Mode:</strong>
                          <span 
                            class="badge ms-2" 
                            :class="room.sendRealMessage ? 'bg-danger bg-opacity-10 text-danger' : 'bg-info bg-opacity-10 text-info'"
                            style="font-size: 0.75rem; padding: 4px 8px;"
                          >
                            <i class="bi me-1" :class="room.sendRealMessage ? 'bi-exclamation-triangle-fill' : 'bi-shield-check'"></i>
                            <span v-if="room.sendRealMessage"><strong>REAL MODE</strong> - Kirim pesan asli</span>
                            <span v-else><strong>DRY RUN</strong> - Mode testing</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Warning for deleted instances -->
                    <div v-if="selectedRoom?.id === room.id && !isRoomInstancesValid(room)" class="p-2 bg-warning bg-opacity-10 border-top border-warning">
                      <div class="d-flex align-items-center justify-content-between gap-2">
                        <div class="d-flex align-items-center gap-2">
                          <i class="bi bi-exclamation-triangle-fill text-warning"></i>
                          <small class="text-warning">
                            <strong>Instance Deleted!</strong> This room cannot be started.
                          </small>
                        </div>
                        <!-- Delete button for invalid instances -->
                        <button
                          class="btn btn-sm btn-danger"
                          @click.stop="deleteRoom(room)"
                          title="Delete this room"
                        >
                          <i class="bi bi-trash"></i> Delete
                        </button>
                      </div>
                    </div>
                    
                    <!-- Quick Actions (only for selected with valid instances) -->
                    <div class="room-card-footer p-3 bg-light border-top" v-if="selectedRoom?.id === room.id && isRoomInstancesValid(room)">
                      <div class="d-flex gap-2">
                        <!-- Show Restart button when FINISHED -->
                        <button
                          v-if="room.status === 'FINISHED'"
                          class="btn btn-sm btn-primary flex-fill"
                          @click.stop="restartRoom(room)"
                          title="Restart"
                        >
                          <i class="bi bi-arrow-clockwise"></i> Restart
                        </button>
                        
                        <!-- Show Start button when STOPPED or PAUSED -->
                        <button
                          v-if="room.status === 'STOPPED' || room.status === 'PAUSED'"
                          class="btn btn-sm btn-success flex-fill"
                          @click.stop="updateStatus(room, 'ACTIVE')"
                          title="Start"
                        >
                          <i class="bi bi-play-fill"></i>
                        </button>
                        
                        <!-- Show Pause button when ACTIVE -->
                        <button
                          v-if="room.status === 'ACTIVE'"
                          class="btn btn-sm btn-warning flex-fill"
                          @click.stop="updateStatus(room, 'PAUSED')"
                          title="Pause"
                        >
                          <i class="bi bi-pause-fill"></i>
                        </button>
                        
                        <!-- Show Stop button when ACTIVE or PAUSED -->
                        <button
                          v-if="room.status === 'ACTIVE' || room.status === 'PAUSED'"
                          class="btn btn-sm btn-secondary flex-fill"
                          @click.stop="updateStatus(room, 'STOPPED')"
                          title="Stop"
                        >
                          <i class="bi bi-stop-fill"></i>
                        </button>
                        
                        <!-- Edit button (always show except when ACTIVE) -->
                        <button
                          v-if="room.status !== 'ACTIVE'"
                          class="btn btn-sm btn-outline-warning flex-fill"
                          @click.stop="editRoom(room)"
                          title="Edit"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        
                        <!-- Delete button (always show except when ACTIVE) -->
                        <button
                          v-if="room.status !== 'ACTIVE'"
                          class="btn btn-sm btn-outline-danger flex-fill"
                          @click.stop="deleteRoom(room)"
                          title="Delete"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- CENTER: Script Lines Preview (33%) -->
        <div class="col-lg-4">
          <div class="card shadow-sm" style="height: calc(100vh - 180px);">
            <div class="card-body p-0 d-flex flex-column h-100">
              <!-- Header -->
              <div class="p-2 border-bottom bg-info text-white">
                <h6 class="mb-0 fw-bold" style="font-size: 0.9rem;">
                  <i class="bi bi-file-text me-1"></i>
                  Script Preview
                </h6>
                <small style="font-size: 0.7rem;" class="opacity-75">Template Messages</small>
              </div>

              <!-- Script Lines -->
              <div class="flex-grow-1 overflow-auto p-2" ref="scriptLinesContainer">
                <!-- Empty State -->
                <div v-if="!selectedRoom" class="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                  <i class="bi bi-file-text" style="font-size: 3rem; opacity: 0.3;"></i>
                  <p class="mt-2 small">Select a room</p>
                </div>

                <!-- Loading -->
                <div v-else-if="loadingLines" class="d-flex align-items-center justify-content-center h-100">
                  <div class="spinner-border spinner-border-sm text-primary"></div>
                </div>

                <!-- Script Lines List -->
                <div v-else-if="scriptLines.length > 0">
                  <div
                    v-for="line in scriptLines"
                    :key="line.id"
                    :ref="'scriptLine-' + line.sequenceOrder"
                    class="script-line-item-enhanced"
                    :class="{ 'current-line': line.sequenceOrder === selectedRoom.currentSequence }"
                  >
                    <div class="line-header-enhanced">
                      <div class="d-flex align-items-center gap-2">
                        <span class="badge bg-secondary" style="font-size: 0.7rem; padding: 3px 8px;">
                          #{{ line.sequenceOrder }}
                        </span>
                        <span class="badge" :class="line.actorRole === 'ACTOR_A' ? 'bg-primary' : 'bg-success'" style="font-size: 0.7rem; padding: 3px 8px;">
                          {{ line.actorRole === 'ACTOR_A' ? 'Send' : 'Recv' }}
                        </span>
                      </div>
                      <span class="badge bg-light text-dark border" style="font-size: 0.7rem; padding: 3px 8px;">
                        <i class="bi bi-stopwatch me-1"></i>{{ line.typingDurationSec }}s
                      </span>
                    </div>
                    <div class="line-content-enhanced">
                      {{ renderMessage(line.messageContent) }}
                    </div>
                  </div>
                </div>

                <!-- No Lines -->
                <div v-else class="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                  <i class="bi bi-inbox" style="font-size: 2rem; opacity: 0.3;"></i>
                  <p class="mt-2 small">No script lines</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: Message Logs (33%) -->
        <div class="col-lg-4">
          <div class="card shadow-sm" style="height: calc(100vh - 180px);">
            <div class="card-body p-0 d-flex flex-column h-100">
              <!-- Header -->
              <div class="p-2 border-bottom bg-success text-white">
                <h6 class="mb-0 fw-bold" style="font-size: 0.9rem;">
                  <i class="bi bi-chat-left-text me-1"></i>
                  Message Logs
                </h6>
                <small style="font-size: 0.7rem;" class="opacity-75">Sent Messages</small>
              </div>

              <!-- Message Logs -->
              <div class="flex-grow-1 overflow-auto p-3 log-container-modern" ref="logsContainer">
                <!-- Empty State -->
                <div v-if="!selectedRoom" class="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                  <i class="bi bi-chat-quote" style="font-size: 3rem; opacity: 0.3;"></i>
                  <p class="mt-2 small">Select a room</p>
                </div>

                <!-- Loading -->
                <div v-else-if="loadingLogs" class="d-flex align-items-center justify-content-center h-100">
                  <div class="spinner-border spinner-border-sm text-primary"></div>
                </div>

                <!-- Modern Log List with Fade Animation -->
                <transition-group 
                  v-if="messageLogs.length > 0" 
                  name="message-fade" 
                  tag="div" 
                  class="log-list-modern"
                >
                  <div
                    v-for="log in sortedMessageLogs"
                    :key="log.id"
                    class="log-item-wrapper"
                    :class="isLogFromSender(log) ? 'log-left' : 'log-right'"
                  >
                    <div class="log-item-modern" :class="{ 'log-failed': log.status !== 'SUCCESS' }">
                      <!-- Log Header -->
                      <div class="log-header-modern">
                        <div class="d-flex align-items-center gap-2 flex-grow-1">
                          <div class="log-avatar-compact" :class="isLogFromSender(log) ? 'avatar-sender' : 'avatar-receiver'">
                            <i class="bi" :class="isLogFromSender(log) ? 'bi-arrow-up-right' : 'bi-arrow-down-left'"></i>
                          </div>
                          <div class="log-meta flex-grow-1">
                            <div class="log-actor-compact">
                              {{ isLogFromSender(log) ? 'Sender' : 'Receiver' }}
                            </div>
                            <div class="log-time-compact">
                              <i class="bi bi-clock me-1"></i>
                              {{ formatLogTime(log.executedAt || log.sentAt) }}
                            </div>
                          </div>
                        </div>
                        <div class="log-status-compact">
                          <span v-if="log.status === 'SUCCESS'" class="badge bg-success text-white" style="font-size: 0.65rem; padding: 2px 6px;">
                            <i class="bi bi-check-circle-fill"></i>
                          </span>
                          <span v-else class="badge bg-danger text-white" style="font-size: 0.65rem; padding: 2px 6px;">
                            <i class="bi bi-x-circle-fill"></i>
                          </span>
                        </div>
                      </div>

                      <!-- Log Content -->
                      <div class="log-content-compact">
                        {{ log.messageContent }}
                      </div>

                      <!-- Log Footer (if error) -->
                      <div v-if="log.errorMessage" class="log-footer-modern">
                        <i class="bi bi-exclamation-triangle me-1"></i>
                        <small>{{ log.errorMessage }}</small>
                      </div>
                    </div>
                  </div>
                </transition-group>

                <!-- No Logs -->
                <div v-else class="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                  <i class="bi bi-inbox" style="font-size: 2rem; opacity: 0.3;"></i>
                  <p class="mt-2 small">No messages sent yet</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- Create/Edit Modal -->
    <div class="modal fade" id="roomModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editMode ? 'Edit Room' : 'Create Room' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveRoom">
              <!-- Name with Icon -->
              <div class="mb-3">
                <label class="form-label d-flex align-items-center gap-2">
                  <i class="bi bi-door-open-fill text-primary"></i>
                  <span>Room Name</span>
                  <span class="text-danger">*</span>
                </label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <i class="bi bi-tag-fill text-primary"></i>
                  </span>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="form.name" 
                    :class="{ 'is-invalid': errors.name }"
                    @input="clearError('name')"
                    placeholder="e.g., Room 1"
                  >
                  <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
                </div>
              </div>
              
              <!-- Room Type Dropdown -->
              <div class="mb-3">
                <label class="form-label d-flex align-items-center gap-2">
                  <i class="bi bi-diagram-3-fill text-primary"></i>
                  <span>Room Type</span>
                  <span class="text-danger">*</span>
                </label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <i class="bi bi-gear-fill text-primary"></i>
                  </span>
                  <select 
                    class="form-select" 
                    v-model="form.roomType"
                    :disabled="editMode"
                  >
                    <option value="BOT_VS_BOT">BOT vs BOT - Dua bot saling chat otomatis</option>
                    <option value="HUMAN_VS_BOT">HUMAN vs BOT - Bot auto-reply ke nomor tertentu</option>
                  </select>
                </div>
                <small class="text-muted d-flex align-items-center gap-1 mt-1">
                  <i class="bi bi-info-circle"></i>
                  <span v-if="editMode" class="text-warning">Room type cannot be changed after creation</span>
                  <span v-else>Pilih tipe room sesuai kebutuhan</span>
                </small>
              </div>
              
              <!-- Instances Row with Enhanced Design -->
              <div class="row">
                <!-- Sender (only for BOT_VS_BOT) -->
                <div v-if="form.roomType === 'BOT_VS_BOT'" class="col-md-6 mb-3">
                  <label class="form-label d-flex align-items-center gap-2">
                    <span class="badge bg-primary bg-opacity-10 text-primary px-2 py-1">
                      <i class="bi bi-send-fill me-1"></i>
                      Sender
                    </span>
                    <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-text bg-primary text-white">
                      <i class="bi bi-arrow-up-right-circle-fill"></i>
                    </span>
                    <select 
                      class="form-select" 
                      v-model="form.senderInstanceId"
                      :class="{ 'is-invalid': errors.senderInstanceId }"
                      @change="clearError('senderInstanceId')"
                    >
                      <option value="">Select Sender Instance</option>
                      <option v-for="instance in instances" :key="instance.id" :value="instance.instanceId">
                        {{ instance.phoneNumber }} (...{{ instance.instanceId.slice(-5) }})
                      </option>
                    </select>
                    <div v-if="errors.senderInstanceId" class="invalid-feedback">{{ errors.senderInstanceId }}</div>
                  </div>
                  <small class="text-muted d-flex align-items-center gap-1 mt-1">
                    <i class="bi bi-info-circle"></i>
                    Instance yang mengirim pesan
                  </small>
                </div>
                
                <!-- Receiver / Bot Instance -->
                <div :class="form.roomType === 'BOT_VS_BOT' ? 'col-md-6' : 'col-md-12'" class="mb-3">
                  <label class="form-label d-flex align-items-center gap-2">
                    <span class="badge bg-success bg-opacity-10 text-success px-2 py-1">
                      <i class="bi me-1" :class="form.roomType === 'HUMAN_VS_BOT' ? 'bi-robot' : 'bi-inbox-fill'"></i>
                      {{ form.roomType === 'HUMAN_VS_BOT' ? 'Bot Instance' : 'Receiver' }}
                    </span>
                    <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-text bg-success text-white">
                      <i class="bi" :class="form.roomType === 'HUMAN_VS_BOT' ? 'bi-cpu-fill' : 'bi-arrow-down-left-circle-fill'"></i>
                    </span>
                    <select 
                      class="form-select" 
                      v-model="form.receiverInstanceId"
                      :class="{ 'is-invalid': errors.receiverInstanceId }"
                      @change="clearError('receiverInstanceId')"
                    >
                      <option value="">{{ form.roomType === 'HUMAN_VS_BOT' ? 'Select Bot Instance' : 'Select Receiver Instance' }}</option>
                      <option v-for="instance in instances" :key="instance.id" :value="instance.instanceId">
                        {{ instance.phoneNumber }} (...{{ instance.instanceId.slice(-5) }})
                      </option>
                    </select>
                    <div v-if="errors.receiverInstanceId" class="invalid-feedback">{{ errors.receiverInstanceId }}</div>
                  </div>
                  <small class="text-muted d-flex align-items-center gap-1 mt-1">
                    <i class="bi bi-info-circle"></i>
                    {{ form.roomType === 'HUMAN_VS_BOT' ? 'Bot instance yang akan auto-reply' : 'Instance yang menerima pesan' }}
                  </small>
                </div>
              </div>

              <!-- Whitelisted Number (only for HUMAN_VS_BOT) -->
              <div v-if="form.roomType === 'HUMAN_VS_BOT'" class="mb-3">
                <label class="form-label d-flex align-items-center gap-2">
                  <i class="bi bi-phone-fill text-primary"></i>
                  <span>Whitelisted Number</span>
                  <span class="text-danger">*</span>
                </label>
                <div class="input-group">
                  <span class="input-group-text bg-light">
                    <i class="bi bi-person-check-fill text-primary"></i>
                  </span>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="form.whitelistedNumber" 
                    :class="{ 'is-invalid': errors.whitelistedNumber }"
                    @input="clearError('whitelistedNumber')"
                    placeholder="e.g., 6281234567890 or 081234567890"
                  >
                  <div v-if="errors.whitelistedNumber" class="invalid-feedback">{{ errors.whitelistedNumber }}</div>
                </div>
                <small class="text-muted d-flex align-items-center gap-1 mt-1">
                  <i class="bi bi-info-circle"></i>
                  Nomor HP manusia yang akan dibalas otomatis (format: 62xxx atau 08xxx)
                </small>
              </div>

              <!-- Script with Icon -->
              <div class="mb-3">
                <label class="form-label d-flex align-items-center gap-2">
                  <i class="bi bi-file-text-fill text-info"></i>
                  <span>Script</span>
                  <span class="text-danger">*</span>
                  <span v-if="form.roomType === 'HUMAN_VS_BOT' && form.useAi" class="badge bg-secondary ms-2" style="font-size: 0.7rem;">Fallback</span>
                </label>
                <div class="input-group">
                  <span class="input-group-text bg-info text-white">
                    <i class="bi bi-chat-left-quote-fill"></i>
                  </span>
                  <select 
                    class="form-select" 
                    v-model="form.scriptId"
                    :class="{ 'is-invalid': errors.scriptId }"
                    @change="clearError('scriptId')"
                  >
                    <option :value="null">Select Script</option>
                    <option v-for="script in scripts" :key="script.id" :value="script.id">
                      {{ script.title }} ({{ script.lineCount || 0 }} lines)
                    </option>
                  </select>
                  <div v-if="errors.scriptId" class="invalid-feedback">{{ errors.scriptId }}</div>
                </div>
              </div>

              <!-- Interval Sliders (BOT_VS_BOT) or Reply Delay (HUMAN_VS_BOT) -->
              <div class="mb-3">
                <label class="form-label d-flex align-items-center gap-2">
                  <i class="bi text-warning" :class="form.roomType === 'HUMAN_VS_BOT' ? 'bi-hourglass-split' : 'bi-clock-fill'"></i>
                  <span v-if="form.roomType === 'HUMAN_VS_BOT'">
                    Reply Delay: <strong>{{ form.replyDelayMin }}s - {{ form.replyDelayMax }}s</strong>
                  </span>
                  <span v-else>
                    Interval Range: <strong>{{ formatTime(form.intervalMinSeconds) }} - {{ formatTime(form.intervalMaxSeconds) }}</strong>
                  </span>
                  <span class="text-danger">*</span>
                </label>
                <div class="row">
                  <div class="col-md-6">
                    <label class="form-label small text-muted">{{ form.roomType === 'HUMAN_VS_BOT' ? 'Min Delay' : 'Min Interval' }}</label>
                    <input
                      v-if="form.roomType === 'HUMAN_VS_BOT'"
                      type="range"
                      class="form-range"
                      v-model.number="form.replyDelayMin"
                      @input="adjustReplyDelayMin"
                      min="1"
                      max="300"
                      step="1"
                    >
                    <input
                      v-else
                      type="range"
                      class="form-range"
                      v-model.number="form.intervalMinSeconds"
                      @input="adjustMinInterval"
                      min="1"
                      max="18000"
                      step="1"
                    >
                    <div class="d-flex justify-content-between">
                      <small class="text-muted">1s</small>
                      <small class="text-muted">{{ form.roomType === 'HUMAN_VS_BOT' ? '5 min' : '5 hours' }}</small>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small text-muted">{{ form.roomType === 'HUMAN_VS_BOT' ? 'Max Delay' : 'Max Interval' }}</label>
                    <input
                      v-if="form.roomType === 'HUMAN_VS_BOT'"
                      type="range"
                      class="form-range"
                      v-model.number="form.replyDelayMax"
                      @input="adjustReplyDelayMax"
                      min="1"
                      max="300"
                      step="1"
                    >
                    <input
                      v-else
                      type="range"
                      class="form-range"
                      v-model.number="form.intervalMaxSeconds"
                      @input="adjustMaxInterval"
                      min="1"
                      max="18000"
                      step="1"
                    >
                    <div class="d-flex justify-content-between">
                      <small class="text-muted">1s</small>
                      <small class="text-muted">{{ form.roomType === 'HUMAN_VS_BOT' ? '5 min' : '5 hours' }}</small>
                    </div>
                  </div>
                </div>
                <div class="alert alert-info d-flex align-items-center py-2 px-3 mt-2 mb-0" style="font-size: 0.85rem;">
                  <i class="bi bi-info-circle-fill me-2"></i>
                  <div v-if="form.roomType === 'HUMAN_VS_BOT'">
                    <strong>Random Delay:</strong> Bot akan menunggu secara random antara 
                    <span class="badge bg-primary">{{ form.replyDelayMin }}s</span> 
                    dan 
                    <span class="badge bg-success">{{ form.replyDelayMax }}s</span>
                    sebelum membalas pesan (seperti manusia).
                  </div>
                  <div v-else>
                    <strong>Random Interval:</strong> Jeda pengiriman pesan akan di-acak secara random antara 
                    <span class="badge bg-primary">{{ formatTime(form.intervalMinSeconds) }}</span> 
                    dan 
                    <span class="badge bg-success">{{ formatTime(form.intervalMaxSeconds) }}</span>
                    untuk menghindari deteksi spam.
                  </div>
                </div>
              </div>


              <!-- AI Configuration (only for HUMAN_VS_BOT) -->
              <div v-if="form.roomType === 'HUMAN_VS_BOT'" class="mb-3">
                <!-- AI Enable Toggle -->
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label mb-0 d-flex align-items-center gap-2">
                    <i class="bi bi-stars text-warning"></i>
                    <span>Enable AI-Powered Replies</span>
                  </label>
                  <div class="form-check form-switch">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      role="switch" 
                      id="aiEnabledSwitch"
                      v-model="form.aiEnabled"
                      style="cursor: pointer; width: 3rem; height: 1.5rem;"
                    >
                  </div>
                </div>
                <div class="alert py-2 px-3 mb-3" :class="form.aiEnabled ? 'alert-success' : 'alert-secondary'" style="font-size: 0.85rem;">
                  <i class="bi me-2" :class="form.aiEnabled ? 'bi-stars' : 'bi-file-text'"></i>
                  <span v-if="form.aiEnabled">
                    <strong>AI Mode:</strong> Bot akan menggunakan AI untuk generate balasan yang natural.
                  </span>
                  <span v-else>
                    <strong>Script Mode:</strong> Bot akan menggunakan script template untuk balasan.
                  </span>
                </div>

                <!-- AI Configuration Fields (show only if AI enabled) -->
                <div v-if="form.aiEnabled" class="border rounded p-3 bg-light">
                  <!-- AI Info (Static) -->
                  <div class="mb-3">
                    <div class="row g-2">
                      <div class="col-6">
                        <label class="form-label small text-muted mb-1">AI Provider</label>
                        <div class="d-flex align-items-center gap-2 px-2 py-1 bg-white border rounded">
                          <i class="bi bi-cloud-fill text-primary"></i>
                          <span class="small fw-bold">Google Gemini</span>
                        </div>
                      </div>
                      <div class="col-6">
                        <label class="form-label small text-muted mb-1">AI Model</label>
                        <div class="d-flex align-items-center gap-2 px-2 py-1 bg-white border rounded">
                          <i class="bi bi-cpu-fill text-info"></i>
                          <span class="small fw-bold">Gemini Flash Latest</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- System Prompt -->
                  <div class="mb-3">
                    <label class="form-label d-flex align-items-center gap-2">
                      <i class="bi bi-chat-left-text-fill text-success"></i>
                      <span>System Prompt</span>
                      <span class="text-danger">*</span>
                    </label>
                    <textarea 
                      class="form-control" 
                      v-model="form.aiSystemPrompt" 
                      :class="{ 'is-invalid': errors.aiSystemPrompt }"
                      @input="clearError('aiSystemPrompt')"
                      rows="4"
                      placeholder="You are a helpful customer service assistant..."
                    ></textarea>
                    <div v-if="errors.aiSystemPrompt" class="invalid-feedback">{{ errors.aiSystemPrompt }}</div>
                    <small class="text-muted d-flex align-items-center gap-1 mt-1">
                      <i class="bi bi-info-circle"></i>
                      Instruksi untuk AI tentang bagaimana cara merespons (persona, tone, dll)
                    </small>
                  </div>

                  <!-- Temperature Slider -->
                  <div class="mb-3">
                    <label class="form-label d-flex align-items-center gap-2">
                      <i class="bi bi-thermometer-half text-warning"></i>
                      <span>Temperature (Creativity): <strong>{{ form.aiTemperature }}</strong></span>
                    </label>
                    <input
                      type="range"
                      class="form-range"
                      v-model.number="form.aiTemperature"
                      :class="{ 'is-invalid': errors.aiTemperature }"
                      min="0"
                      max="1"
                      step="0.1"
                    >
                    <div v-if="errors.aiTemperature" class="invalid-feedback">{{ errors.aiTemperature }}</div>
                    <div class="d-flex justify-content-between">
                      <small class="text-muted">0.0 (Focused)</small>
                      <small class="text-muted">1.0 (Creative)</small>
                    </div>
                    <small class="text-muted d-flex align-items-center gap-1 mt-1">
                      <i class="bi bi-info-circle"></i>
                      Lower = lebih fokus dan konsisten, Higher = lebih kreatif dan bervariasi
                    </small>
                  </div>

                  <!-- Max Tokens -->
                  <div class="mb-3">
                    <label class="form-label d-flex align-items-center gap-2">
                      <i class="bi bi-hash text-secondary"></i>
                      <span>Max Response Length (Tokens)</span>
                      <span class="text-danger">*</span>
                    </label>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model.number="form.aiMaxTokens"
                      :class="{ 'is-invalid': errors.aiMaxTokens }"
                      @input="clearError('aiMaxTokens')"
                      min="50"
                      max="500"
                      step="10"
                    >
                    <div v-if="errors.aiMaxTokens" class="invalid-feedback">{{ errors.aiMaxTokens }}</div>
                    <small class="text-muted d-flex align-items-center gap-1 mt-1">
                      <i class="bi bi-info-circle"></i>
                      Maksimal panjang balasan AI (50-500 tokens, ~1 token = 4 karakter)
                    </small>
                  </div>

                  <!-- Fallback to Script -->
                  <div class="mb-0">
                    <div class="d-flex justify-content-between align-items-center">
                      <label class="form-label mb-0 d-flex align-items-center gap-2">
                        <i class="bi bi-shield-check text-primary"></i>
                        <span>Fallback to Script if AI Fails</span>
                      </label>
                      <div class="form-check form-switch">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          role="switch" 
                          id="fallbackSwitch"
                          v-model="form.fallbackToScript"
                          style="cursor: pointer; width: 3rem; height: 1.5rem;"
                        >
                      </div>
                    </div>
                    <small class="text-muted d-flex align-items-center gap-1 mt-1">
                      <i class="bi bi-info-circle"></i>
                      Jika AI error, gunakan script sebagai fallback (recommended untuk production)
                    </small>
                  </div>
                </div>
              </div>
              
              <!-- Send Real Message Toggle -->
              <div class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label mb-0">Send Real WhatsApp Messages</label>
                  <div class="form-check form-switch">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      role="switch" 
                      id="sendRealMessageSwitch"
                      v-model="form.sendRealMessage"
                      style="cursor: pointer; width: 3rem; height: 1.5rem;"
                    >
                  </div>
                </div>
                <div class="alert py-2 px-3 mb-0" :class="form.sendRealMessage ? 'alert-warning' : 'alert-info'" style="font-size: 0.85rem;">
                  <i class="bi me-2" :class="form.sendRealMessage ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'"></i>
                  <span v-if="form.sendRealMessage">
                    <strong>Real Mode:</strong> Pesan akan dikirim ke WhatsApp secara nyata.
                  </span>
                  <span v-else>
                    <strong>Dry Run Mode:</strong> Hanya testing, pesan tidak akan dikirim ke WhatsApp.
                  </span>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-chatroom btn-secondary-chatroom" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-sm btn-chatroom btn-primary-chatroom" @click="saveRoom">
              <i class="bi bi-save"></i> Save
            </button>
          </div>
        </div>
      </div>
    </div>

  </main>
</template>

<script>
import axios from 'axios';
import { useSystem } from '@/composables/useSystem.js';

export default {
  setup() {
    const { themeColor } = useSystem();
    return { themeColor };
  },
  data() {
    return {
      rooms: [],
      loading: false,
      statusFilter: '',
      baseUrl: process.env.VUE_APP_BE_APP_BASE_URL,
      
      editMode: false,
      currentRoom: null,
      form: {
        name: '',
        roomType: 'BOT_VS_BOT', // Default to BOT_VS_BOT
        senderInstanceId: '',
        receiverInstanceId: '',
        scriptId: null,
        intervalMinSeconds: 5,
        intervalMaxSeconds: 15,
        sendRealMessage: false,
        // HUMAN_VS_BOT specific fields
        whitelistedNumber: '',
        replyDelayMin: 15,
        replyDelayMax: 45,
        // AI Configuration (new structure)
        aiEnabled: false,
        aiProvider: 'gemini',
        aiModel: 'gemini-1.5-flash',
        aiSystemPrompt: 'You are a helpful customer service assistant. Be friendly, concise, and professional.',
        aiTemperature: 0.7,
        aiMaxTokens: 150,
        fallbackToScript: true
      },
      
      // Dropdown Data
      instances: [],
      scripts: [],
      
      // Validation
      errors: {
        name: '',
        senderInstanceId: '',
        receiverInstanceId: '',
        scriptId: '',
        whitelistedNumber: '',
        aiSystemPrompt: '',
        aiTemperature: '',
        aiMaxTokens: ''
      },
      
      // Preview
      selectedRoom: null,
      scriptLines: [],
      loadingLines: false,
      
      // Message Logs
      messageLogs: [],
      loadingLogs: false,
      
      // WebSocket
      ws: null,
      wsConnected: false,
      wsRetryCount: 0,
      wsRetryTimeout: null
    };
  },
  
  computed: {
    // Format instance display: phone number (...last 5 chars of instanceId)
    formatInstanceDisplay() {
      return (instanceId) => {
        if (!instanceId) return 'N/A';
        
        // Find instance in instances array
        const instance = this.instances.find(i => i.instanceId === instanceId);
        
        if (!instance) {
          // Instance deleted/not found - show last 5 chars only
          const last5 = instanceId.slice(-5);
          return `[Deleted] (...${last5})`;
        }
        
        // Format: phoneNumber (...last5chars)
        const phoneNumber = instance.phoneNumber || 'Unknown';
        const last5 = instanceId.slice(-5);
        return `${phoneNumber} (...${last5})`;
      };
    },
    
    // Format interval display: convert seconds to readable time
    formatIntervalDisplay() {
      return (minSec, maxSec) => {
        const formatTime = (seconds) => {
          if (seconds < 60) {
            return `${seconds}s`;
          } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
          } else {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
          }
        };
        
        return `${formatTime(minSec)} - ${formatTime(maxSec)}`;
      };
    },
    
    // Check if room has valid instances (both sender and receiver exist)
    isRoomInstancesValid() {
      return (room) => {
        // For HUMAN_VS_BOT, only check sender (bot instance)
        if (room.roomType === 'HUMAN_VS_BOT') {
          if (!room.senderInstanceId) return false;
          return this.instances.some(i => i.instanceId === room.senderInstanceId);
        }
        
        // For BOT_VS_BOT, check both sender and receiver
        if (!room.senderInstanceId || !room.receiverInstanceId) return false;
        
        const senderExists = this.instances.some(i => i.instanceId === room.senderInstanceId);
        const receiverExists = this.instances.some(i => i.instanceId === room.receiverInstanceId);
        
        return senderExists && receiverExists;
      };
    },
    
    // Sort message logs by executedAt (oldest first, like WhatsApp)
    sortedMessageLogs() {
      return [...this.messageLogs].sort((a, b) => {
        const timeA = new Date(a.executedAt || a.sentAt || 0);
        const timeB = new Date(b.executedAt || b.sentAt || 0);
        return timeA - timeB; // Ascending order (oldest first)
      });
    }
  },
  
  mounted() {
    this.fetchRooms();
    // Setup WebSocket for real-time updates
    this.setupWebSocket();
  },
  
  beforeUnmount() {
    // Cleanup WebSocket
    if (this.wsRetryTimeout) {
      clearTimeout(this.wsRetryTimeout);
      this.wsRetryTimeout = null;
    }
    if (this.ws) {
      console.log('Closing WebSocket...');
      this.ws.close();
      this.ws = null;
    }
  },
  
  methods: {
    async fetchRooms() {
      this.loading = true;
      try {
        const params = this.statusFilter ? { status: this.statusFilter } : {};
        const res = await axios.get(`${this.baseUrl}/api/warming/rooms`, { params });
        this.rooms = res.data.data.rooms || [];
        
        // Fetch instances and scripts for display formatting
        await this.fetchInstances();
        await this.fetchScripts();
        
        // Enrich rooms with script titles and total lines
        this.rooms = await Promise.all(this.rooms.map(async (room) => {
          const script = this.scripts.find(s => s.id === room.scriptId);
          let totalLines = 0;
          
          // Fetch actual line count from script lines API
          if (script) {
            try {
              const linesRes = await axios.get(`${this.baseUrl}/api/warming/scripts/${script.id}/lines`);
              totalLines = linesRes.data.data.lines?.length || 0;
            } catch (error) {
              console.error(`Failed to fetch lines for script ${script.id}:`, error);
              totalLines = 0;
            }
          }
          
          return {
            ...room,
            scriptTitle: script ? script.title : 'N/A',
            totalLines: totalLines
          };
        }));
      } catch (error) {
        console.error('Failed to fetch rooms:', error);
        this.$swal.fire('Error', 'Failed to load rooms', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    selectRoom(room) {
      this.selectedRoom = room;
      
      // Fetch script lines
      if (room.scriptId) {
        this.fetchScriptLines(room.scriptId);
      } else {
        this.scriptLines = [];
      }
      
      // Fetch message logs
      this.fetchMessageLogs(room.id);
      
      // Polling disabled - will use websocket instead
      // this.stopLogsPolling();
      // if (room.status === 'ACTIVE') {
      //   this.startLogsPolling();
      // }
    },
    
    async fetchScriptLines(scriptId) {
      this.loadingLines = true;
      try {
        const res = await axios.get(`${this.baseUrl}/api/warming/scripts/${scriptId}/lines`);
        this.scriptLines = res.data.data.lines || [];
      } catch (error) {
        console.error('Failed to fetch script lines:', error);
        this.scriptLines = [];
      } finally {
        this.loadingLines = false;
      }
    },
    
    renderMessage(content) {
      if (!content) return '(empty)';
      
      let text = content;
      
      // Replace variables
      const now = new Date();
      const hour = now.getHours();
      const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
      const dayName = dayNames[now.getDay()];
      const date = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      
      let timeGreeting = 'Selamat Pagi';
      if (hour >= 11 && hour < 15) timeGreeting = 'Selamat Siang';
      else if (hour >= 15 && hour < 18) timeGreeting = 'Selamat Sore';
      else if (hour >= 18 || hour < 4) timeGreeting = 'Selamat Malam';
      
      text = text.replace(/{TIME_GREETING}/g, timeGreeting);
      text = text.replace(/{DAY_NAME}/g, dayName);
      text = text.replace(/{DATE}/g, date);
      
      // Render spintax - pick random option
      text = text.replace(/{([^}]+)}/g, (match, content) => {
        const options = content.split('|');
        return options[Math.floor(Math.random() * options.length)];
      });
      
      return text;
    },
    
    async fetchInstances() {
      try {
        const res = await axios.get(`${this.baseUrl}/api/instances`);
        this.instances = res.data.data.instances || [];
      } catch (error) {
        console.error('Failed to fetch instances:', error);
        this.instances = [];
      }
    },
    
    async fetchScripts() {
      try {
        const res = await axios.get(`${this.baseUrl}/api/warming/scripts`);
        const scriptsData = res.data.data.scripts || [];
        
        // Fetch line count for each script
        this.scripts = await Promise.all(scriptsData.map(async (script) => {
          try {
            const linesRes = await axios.get(`${this.baseUrl}/api/warming/scripts/${script.id}/lines`);
            const lineCount = linesRes.data.data.lines?.length || 0;
            
            return {
              ...script,
              lineCount: lineCount
            };
          } catch (error) {
            console.error(`Failed to fetch lines for script ${script.id}:`, error);
            return {
              ...script,
              lineCount: 0
            };
          }
        }));
      } catch (error) {
        console.error('Failed to fetch scripts:', error);
        this.scripts = [];
      }
    },
    
    validateForm() {
      this.errors = {
        name: '',
        senderInstanceId: '',
        receiverInstanceId: '',
        scriptId: '',
        whitelistedNumber: '',
        aiContext: ''
      };
      
      let isValid = true;
      
      // Common validation
      if (!this.form.name || this.form.name.trim() === '') {
        this.errors.name = 'Name is required';
        isValid = false;
      }
      
      if (!this.form.scriptId) {
        this.errors.scriptId = 'Script is required';
        isValid = false;
      }
      
      // Room type specific validation
      if (this.form.roomType === 'BOT_VS_BOT') {
        // BOT_VS_BOT: require both sender and receiver
        if (!this.form.senderInstanceId || this.form.senderInstanceId === '') {
          this.errors.senderInstanceId = 'Sender instance is required for BOT_VS_BOT';
          isValid = false;
        }
        
        if (!this.form.receiverInstanceId || this.form.receiverInstanceId === '') {
          this.errors.receiverInstanceId = 'Receiver instance is required';
          isValid = false;
        }
        
        // Validate interval range
        if (this.form.intervalMinSeconds >= this.form.intervalMaxSeconds) {
          this.$swal.fire('Validation Error', 'Min interval must be less than max interval', 'warning');
          isValid = false;
        }
      } else if (this.form.roomType === 'HUMAN_VS_BOT') {
        // HUMAN_VS_BOT: require only receiver and whitelisted number
        if (!this.form.receiverInstanceId || this.form.receiverInstanceId === '') {
          this.errors.receiverInstanceId = 'Receiver instance is required';
          isValid = false;
        }
        
        if (!this.form.whitelistedNumber || this.form.whitelistedNumber.trim() === '') {
          this.errors.whitelistedNumber = 'Whitelisted number is required for HUMAN_VS_BOT';
          isValid = false;
        } else {
          // Validate phone number format (should start with 62 or 08)
          const phoneRegex = /^(62|08)\d{8,13}$/;
          if (!phoneRegex.test(this.form.whitelistedNumber.trim())) {
            this.errors.whitelistedNumber = 'Invalid phone number format (use 62xxx or 08xxx)';
            isValid = false;
          }
        }
        
        // Validate reply delay range
        if (this.form.replyDelayMin >= this.form.replyDelayMax) {
          this.$swal.fire('Validation Error', 'Min reply delay must be less than max reply delay', 'warning');
          isValid = false;
        }
        
        // Validate AI context if AI is enabled
        if (this.form.useAi && (!this.form.aiContext || this.form.aiContext.trim() === '')) {
          this.errors.aiContext = 'AI context is required when AI mode is enabled';
          isValid = false;
        }
      }
      
      return isValid;
    },
    
    clearError(field) {
      this.errors[field] = '';
    },
    
    
    async fetchMessageLogs(roomId) {
      this.loadingLogs = true;
      try {
        const res = await axios.get(`${this.baseUrl}/api/warming/logs`, {
          params: {
            roomId: roomId
          }
        });
        this.messageLogs = res.data.data.logs || [];
        
        // Auto-scroll to bottom with delay to ensure DOM is rendered
        this.$nextTick(() => {
          setTimeout(() => {
            const container = this.$refs.logsContainer;
            if (container) {
              container.scrollTop = container.scrollHeight;
            }
          }, 100);
        });
      } catch (error) {
        console.error('Failed to fetch logs:', error);
        this.messageLogs = [];
      } finally {
        this.loadingLogs = false;
      }
    },
    
    formatLogTime(timestamp) {
      if (!timestamp) return 'N/A';
      
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      // Less than 1 minute ago
      if (diffMins < 1) return 'Just now';
      
      // Less than 60 minutes ago
      if (diffMins < 60) return `${diffMins}m ago`;
      
      // Less than 24 hours ago
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      
      // Show date and time
      return date.toLocaleString('id-ID', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    isLogFromSender(log) {
      // Check if log is from sender by comparing log's senderInstanceId with room's senderInstanceId
      if (!this.selectedRoom) return false;
      
      // Compare log's senderInstanceId with room's senderInstanceId
      // If they match, this log was sent BY the sender (left side)
      // If they don't match, this log was sent BY the receiver (right side)
      return log.senderInstanceId === this.selectedRoom.senderInstanceId;
    },
    
    formatTime(seconds) {
      if (seconds < 60) {
        return `${seconds}s`;
      } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
      } else {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
      }
    },
    
    adjustMinInterval() {
      // If min becomes >= max, auto-adjust max to min + 1
      if (this.form.intervalMinSeconds >= this.form.intervalMaxSeconds) {
        this.form.intervalMaxSeconds = this.form.intervalMinSeconds + 1;
      }
    },
    
    adjustMaxInterval() {
      // If max becomes <= min, auto-adjust min to max - 1
      if (this.form.intervalMaxSeconds <= this.form.intervalMinSeconds) {
        this.form.intervalMinSeconds = Math.max(1, this.form.intervalMaxSeconds - 1);
      }
    },
    
    adjustReplyDelayMin() {
      // If min becomes >= max, auto-adjust max to min + 1
      if (this.form.replyDelayMin >= this.form.replyDelayMax) {
        this.form.replyDelayMax = Math.min(300, this.form.replyDelayMin + 1);
      }
    },
    
    adjustReplyDelayMax() {
      // If max becomes <= min, auto-adjust min to max - 1
      if (this.form.replyDelayMax <= this.form.replyDelayMin) {
        this.form.replyDelayMin = Math.max(1, this.form.replyDelayMax - 1);
      }
    },
    
    async openCreateModal() {
      this.editMode = false;
      this.currentRoom = null;
      this.form = {
        name: '',
        roomType: 'BOT_VS_BOT',
        senderInstanceId: '',
        receiverInstanceId: '',
        scriptId: null,
        intervalMinSeconds: 5,
        intervalMaxSeconds: 15,
        sendRealMessage: false,
        whitelistedNumber: '',
        replyDelayMin: 15,
        replyDelayMax: 45,
        // AI Configuration
        aiEnabled: false,
        aiProvider: 'gemini',
        aiModel: 'gemini-flash-latest',
        aiSystemPrompt: 'You are a helpful customer service assistant. Be friendly, concise, and professional.',
        aiTemperature: 0.7,
        aiMaxTokens: 150,
        fallbackToScript: true
      };
      this.errors = {
        name: '',
        senderInstanceId: '',
        receiverInstanceId: '',
        scriptId: '',
        whitelistedNumber: '',
        aiSystemPrompt: '',
        aiTemperature: '',
        aiMaxTokens: ''
      };
      
      // Fetch dropdown data
      await this.fetchInstances();
      await this.fetchScripts();
      
      const modal = new bootstrap.Modal(document.getElementById('roomModal'));
      modal.show();
    },
    
    async editRoom(room) {
      this.editMode = true;
      this.currentRoom = room;
      
      const isHumanVsBot = (room.roomType || room.type) === 'HUMAN_VS_BOT';
      
      this.form = {
        name: room.name,
        roomType: room.roomType || 'BOT_VS_BOT',
        senderInstanceId: room.senderInstanceId || '',
        // For HUMAN_VS_BOT, the bot instance is stored in senderInstanceId in backend
        // but the frontend form uses receiverInstanceId for primary instance selection
        receiverInstanceId: isHumanVsBot ? (room.senderInstanceId || room.receiverInstanceId) : room.receiverInstanceId,
        scriptId: room.scriptId,
        intervalMinSeconds: room.intervalMinSeconds,
        intervalMaxSeconds: room.intervalMaxSeconds,
        sendRealMessage: room.sendRealMessage || false,
        whitelistedNumber: room.whitelistedNumber || '',
        replyDelayMin: room.replyDelayMin || 15,
        replyDelayMax: room.replyDelayMax || 45,
        // AI Configuration
        aiEnabled: !!room.aiEnabled, // Ensure boolean
        aiProvider: 'gemini',
        aiModel: 'gemini-flash-latest',
        aiSystemPrompt: room.aiSystemPrompt || 'You are a helpful customer service assistant. Be friendly, concise, and professional.',
        aiTemperature: room.aiTemperature !== undefined ? room.aiTemperature : 0.7,
        aiMaxTokens: room.aiMaxTokens || 150,
        fallbackToScript: room.fallbackToScript !== undefined ? room.fallbackToScript : true
      };
      this.errors = {
        name: '',
        senderInstanceId: '',
        receiverInstanceId: '',
        scriptId: '',
        whitelistedNumber: '',
        aiSystemPrompt: '',
        aiTemperature: '',
        aiMaxTokens: ''
      };
      
      // Fetch dropdown data
      await this.fetchInstances();
      await this.fetchScripts();
      
      const modal = new bootstrap.Modal(document.getElementById('roomModal'));
      modal.show();
    },
    
    async saveRoom() {
      if (!this.validateForm()) {
        return;
      }
      
      // Confirmation popup based on sendRealMessage mode
      const modeText = this.form.sendRealMessage 
        ? '<strong style="color: #dc3545;">REAL MODE</strong><br>Pesan akan dikirim ke WhatsApp secara nyata!' 
        : '<strong style="color: #0d6efd;">DRY RUN MODE</strong><br>Hanya testing, pesan tidak akan dikirim ke WhatsApp.';
      
      const result = await this.$swal.fire({
        title: 'Konfirmasi Simpan Room',
        html: `
          <div style="text-align: left; padding: 10px;">
            <p><strong>Nama Room:</strong> ${this.form.name}</p>
            <hr>
            <p style="font-size: 1.1rem; text-align: center; margin: 15px 0;">
              ${modeText}
            </p>
          </div>
        `,
        icon: this.form.sendRealMessage ? 'warning' : 'info',
        showCancelButton: true,
        confirmButtonText: 'Ya, Simpan',
        cancelButtonText: 'Batal',
        confirmButtonColor: this.form.sendRealMessage ? '#dc3545' : this.themeColor,
        cancelButtonColor: '#6c757d'
      });
      
      if (!result.isConfirmed) {
        return;
      }
      
      this.loading = true;
      try {
        if (this.editMode) {
          // For update, do NOT send roomType (immutable field)
          const updatePayload = {
            name: this.form.name,
            scriptId: this.form.scriptId,
            sendRealMessage: this.form.sendRealMessage
          };
          
          // Add room-type specific fields
          if (this.currentRoom.roomType === 'BOT_VS_BOT') {
            updatePayload.senderInstanceId = this.form.senderInstanceId;
            updatePayload.receiverInstanceId = this.form.receiverInstanceId;
            updatePayload.intervalMinSeconds = this.form.intervalMinSeconds;
            updatePayload.intervalMaxSeconds = this.form.intervalMaxSeconds;
          } else if (this.currentRoom.roomType === 'HUMAN_VS_BOT') {
            updatePayload.senderInstanceId = this.form.receiverInstanceId;
            updatePayload.whitelistedNumber = this.form.whitelistedNumber;
            updatePayload.replyDelayMin = this.form.replyDelayMin;
            updatePayload.replyDelayMax = this.form.replyDelayMax;
            // AI Configuration
            updatePayload.aiEnabled = this.form.aiEnabled;
            if (this.form.aiEnabled) {
              updatePayload.aiProvider = this.form.aiProvider;
              updatePayload.aiModel = this.form.aiModel;
              updatePayload.aiSystemPrompt = this.form.aiSystemPrompt;
              updatePayload.aiTemperature = this.form.aiTemperature;
              updatePayload.aiMaxTokens = this.form.aiMaxTokens;
              updatePayload.fallbackToScript = this.form.fallbackToScript;
            }
          }
          
          await axios.put(`${this.baseUrl}/api/warming/rooms/${this.currentRoom.id}`, updatePayload);
          this.$swal.fire({
            title: 'Success',
            text: 'Room updated!',
            icon: 'success',
            confirmButtonColor: this.themeColor
          });
        } else {
          // For create, construct payload based on room type
          const createPayload = {
            name: this.form.name,
            roomType: this.form.roomType,
            scriptId: this.form.scriptId,
            sendRealMessage: this.form.sendRealMessage
          };
          
          if (this.form.roomType === 'BOT_VS_BOT') {
            createPayload.senderInstanceId = this.form.senderInstanceId;
            createPayload.receiverInstanceId = this.form.receiverInstanceId;
            createPayload.intervalMinSeconds = this.form.intervalMinSeconds;
            createPayload.intervalMaxSeconds = this.form.intervalMaxSeconds;
          } else if (this.form.roomType === 'HUMAN_VS_BOT') {
            createPayload.senderInstanceId = this.form.receiverInstanceId;
            createPayload.whitelistedNumber = this.form.whitelistedNumber;
            createPayload.replyDelayMin = this.form.replyDelayMin;
            createPayload.replyDelayMax = this.form.replyDelayMax;
            // AI Configuration
            createPayload.aiEnabled = this.form.aiEnabled;
            if (this.form.aiEnabled) {
              createPayload.aiProvider = this.form.aiProvider;
              createPayload.aiModel = this.form.aiModel;
              createPayload.aiSystemPrompt = this.form.aiSystemPrompt;
              createPayload.aiTemperature = this.form.aiTemperature;
              createPayload.aiMaxTokens = this.form.aiMaxTokens;
              createPayload.fallbackToScript = this.form.fallbackToScript;
            }
          }
          
          await axios.post(`${this.baseUrl}/api/warming/rooms`, createPayload);
          this.$swal.fire({
            title: 'Success',
            text: 'Room created!',
            icon: 'success',
            confirmButtonColor: this.themeColor
          });

        }
        
        bootstrap.Modal.getInstance(document.getElementById('roomModal')).hide();
        this.fetchRooms();
      } catch (error) {
        console.error('Save error:', error);
        
        // Extract detailed error message
        let errorMessage = 'Failed to save';
        if (error.response?.data) {
          const data = error.response.data;
          // Check if there's a detailed error message
          if (data.error?.details) {
            errorMessage = data.error.details;
          } else if (data.message) {
            errorMessage = data.message;
          }
        }
        
        this.$swal.fire('Error', errorMessage, 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async updateStatus(room, newStatus) {
      try {
        await axios.patch(`${this.baseUrl}/api/warming/rooms/${room.id}/status`, { status: newStatus });
        this.$swal.fire({
          title: 'Success',
          text: `Room ${newStatus.toLowerCase()}!`,
          icon: 'success',
          confirmButtonColor: this.themeColor
        });

        this.fetchRooms();
      } catch (error) {
        console.error('Update status error:', error);
        
        // Extract detailed error message
        let errorMessage = 'Failed to update status';
        if (error.response?.data) {
          const data = error.response.data;
          // Check if there's a detailed error message
          if (data.error?.details) {
            errorMessage = data.error.details;
          } else if (data.message) {
            errorMessage = data.message;
          }
        }
        
        this.$swal.fire('Error', errorMessage, 'error');
      }
    },
    
    async restartRoom(room) {
      const result = await this.$swal.fire({
        title: 'Restart Room?',
        text: 'This will reset progress to the beginning',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, restart'
      });
      
      if (!result.isConfirmed) return;
      
      try {
        await axios.post(`${this.baseUrl}/api/warming/rooms/${room.id}/restart`);
        this.$swal.fire({
          title: 'Success',
          text: 'Room restarted!',
          icon: 'success',
          confirmButtonColor: this.themeColor
        });

        this.fetchRooms();
      } catch (error) {
        this.$swal.fire('Error', 'Failed to restart', 'error');
      }
    },
    
    async deleteRoom(room) {
      const result = await this.$swal.fire({
        title: 'Delete Room?',
        text: `Delete "${room.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Yes, delete'
      });
      
      if (!result.isConfirmed) return;
      
      try {
        await axios.delete(`${this.baseUrl}/api/warming/rooms/${room.id}`);
        this.$swal.fire({
          title: 'Deleted!',
          text: 'Room deleted',
          icon: 'success',
          confirmButtonColor: this.themeColor
        });


        this.fetchRooms();
      } catch (error) {
        this.$swal.fire('Error', 'Failed to delete', 'error');
      }
    },
    
    // WebSocket Setup
    setupWebSocket() {
      if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
        console.log('WebSocket already connected/connecting, skip new connection');
        return;
      }
      
      const wsUrl = process.env.VUE_APP_WS_APP_BASE_URL || 'ws://localhost:2121/ws';
      //const wsUrl = 'ws://192.168.0.40:2121/ws'|| 'ws://localhost:2121/ws';
      console.log('Connecting to WebSocket:', wsUrl, `(retry ${this.wsRetryCount})`);
      
      try {
        this.ws = new WebSocket(wsUrl);
        
        // on open
        this.ws.addEventListener('open', () => {
          console.log('✓ WebSocket connected');
          this.wsConnected = true;
          this.wsRetryCount = 0;
        });
        
        // on message
        this.ws.addEventListener('message', (event) => {
          console.log('WebSocket message:', event.data);
          this.handleWebSocketMessage(event);
        });
        
        // on close
        this.ws.addEventListener('close', () => {
          console.log('✗ WebSocket disconnected');
          this.wsConnected = false;
          
          if (this.wsRetryTimeout) {
            clearTimeout(this.wsRetryTimeout);
            this.wsRetryTimeout = null;
          }
          
          if (this.wsRetryCount < 10) {
            this.wsRetryCount++;
            console.log(`Reconnecting WebSocket in 5s... (attempt ${this.wsRetryCount}/10)`);
            
            this.wsRetryTimeout = setTimeout(() => {
              this.setupWebSocket();
            }, 5000);
          } else {
            console.log('⚠️ WebSocket retry limit reached (10 attempts). Stopping reconnection.');
          }
        });
        
        // on error
        this.ws.addEventListener('error', (error) => {
          console.error('WebSocket error:', error);
          this.wsConnected = false;
        });
        
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
      }
    },
    
    // Handle WebSocket Messages
    handleWebSocketMessage(event) {
      try {
        const wsData = JSON.parse(event.data);
        console.log('Parsed WebSocket data:', wsData);
        
        // Handle warming_message event
        if (wsData.event === 'warming_message') {
          
          // Check if this message belongs to the currently selected room
          if (this.selectedRoom && wsData.data.room_id === this.selectedRoom.id) {
            // Create log object from WebSocket data
            const newLog = {
              id: `ws-${Date.now()}-${Math.random()}`, // Generate temporary ID
              roomId: wsData.data.room_id,
              senderInstanceId: wsData.data.sender_instance_id,
              receiverInstanceId: wsData.data.receiver_instance_id,
              messageContent: wsData.data.message,
              sequenceOrder: wsData.data.sequence_order,
              actorRole: wsData.data.actor_role,
              status: wsData.data.status,
              errorMessage: wsData.data.error_message || '',
              executedAt: wsData.data.timestamp || wsData.timestamp,
              sentAt: wsData.data.timestamp || wsData.timestamp
            };
            
            // Check if log already exists (prevent duplicates)
            const exists = this.messageLogs.some(log => 
              log.sequenceOrder === newLog.sequenceOrder && 
              log.senderInstanceId === newLog.senderInstanceId &&
              log.executedAt === newLog.executedAt
            );
            
            if (!exists) {
              // Add new log to the list
              this.messageLogs.push(newLog);
              
              // Auto-scroll to bottom
              this.$nextTick(() => {
                setTimeout(() => {
                  const container = this.$refs.logsContainer;
                  if (container) {
                    container.scrollTop = container.scrollHeight;
                  }
                }, 100);
              });
            }
          }
          
          // Update room's current sequence if this is for any room in the list
          const roomIndex = this.rooms.findIndex(r => r.id === wsData.data.room_id);
          if (roomIndex !== -1) {
            this.rooms[roomIndex].currentSequence = wsData.data.sequence_order;
            
            // Check if status is FINISHED from backend
            if (wsData.data.status === 'FINISHED') {
              console.log('Room finished, updating status to FINISHED');
              this.rooms[roomIndex].status = 'FINISHED';
              
              // Update selected room if it's the same
              if (this.selectedRoom && this.selectedRoom.id === wsData.data.room_id) {
                this.selectedRoom.status = 'FINISHED';
              }
            }
            
            // Update selected room if it's the same
            if (this.selectedRoom && this.selectedRoom.id === wsData.data.room_id) {
              this.selectedRoom.currentSequence = wsData.data.sequence_order;
              
              // Auto-scroll to current line in script preview
              this.scrollToCurrentLine();
            }
          }
        }
        
        // Handle room_status_changed event (if backend sends it)
        if (wsData.event === 'room_status_changed') {
          console.log('room_status_changed event received:', wsData.data);
          
          const roomIndex = this.rooms.findIndex(r => r.id === wsData.data.room_id);
          if (roomIndex !== -1) {
            this.rooms[roomIndex].status = wsData.data.status;
            
            // Update selected room if it's the same
            if (this.selectedRoom && this.selectedRoom.id === wsData.data.room_id) {
              this.selectedRoom.status = wsData.data.status;
            }
          }
        }
        
      } catch (error) {
        console.error('WebSocket parse error:', error);
      }
    },
    
    // Auto-scroll script preview to current executing line
    scrollToCurrentLine() {
      if (!this.selectedRoom || !this.selectedRoom.currentSequence) return;
      
      this.$nextTick(() => {
        setTimeout(() => {
          const container = this.$refs.scriptLinesContainer;
          const refKey = `scriptLine-${this.selectedRoom.currentSequence}`;
          const element = this.$refs[refKey];
          
          if (container && element && element[0]) {
            const elementTop = element[0].offsetTop;
            const containerHeight = container.clientHeight;
            const elementHeight = element[0].clientHeight;
            
            // Scroll to center the current line
            container.scrollTop = elementTop - (containerHeight / 2) + (elementHeight / 2);
          }
        }, 150);
      });
    }
  }
};
</script>

<style scoped>
/* Fade-in animation for new messages */
.message-fade-enter-active {
  animation: fadeInUp 0.5s ease-out;
}

.message-fade-leave-active {
  animation: fadeOut 0.3s ease-in;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

/* Theme highlight for current executing line */
.script-line-item-enhanced.current-line {
  background: var(--primary-light) !important;
  border-left: 4px solid var(--primary-color) !important;
  box-shadow: 0 2px 8px var(--primary-light) !important;
  transition: all 0.3s ease;
}

.script-line-item-enhanced.current-line .line-header-enhanced {
  background: var(--primary-light) !important;
}

/* Smooth transitions for script lines */
.script-line-item-enhanced {
  transition: all 0.3s ease;
}

/* Active Room Animations */
.room-card-detailed {
  transition: all 0.3s ease;
}

/* Pulse animation for active rooms (using theme color) */
@keyframes activePulse {
  0%, 100% {
    box-shadow: 0 0 0 0 var(--primary-light);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(0, 0, 0, 0);
  }
}

/* Glow animation for active room header (using theme color) */
@keyframes activeGlow {
  0%, 100% {
    box-shadow: 0 0 15px var(--primary-color);
  }
  50% {
    box-shadow: 0 0 25px var(--primary-color);
  }
}

/* Apply animations to active rooms */
.bg-gradient-success {
  animation: activeGlow 2s ease-in-out infinite;
}

.room-card-detailed:has(.bg-gradient-success) {
  animation: activePulse 2s ease-in-out infinite;
  border-color: var(--primary-color) !important;
  border-width: 2px !important;
}

/* Enhance hover effect for active rooms */
.room-card-detailed:has(.bg-gradient-success):hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 20px var(--primary-light);
}
</style>
